/* Çerçevo Kinetik Katalog — tema davranışı.
   ------------------------------------------------------------------------------
   v5.1 (9 Eylül 2026) — BİRLEŞTİRME SÜRÜMÜ + geçiş düzeltmesi.
   Bu dosya iki ayrı soy ağacının birleşimidir:
     (A) canlı temadaki v4.5 (11. tur) — mobil dokunmatik düzeltmeleri ve dengeli
         mozaik dağılımı buradan geldi;
     (B) yerel V5 tasarım paketi — sunum modları (gallery/encounter/curation),
         oda kompozisyonu, sabitlenen duvar slotları ve sayfalı besleme buradan geldi.
   Ayrıca bu sürümde üç yeni şey var: HİBRİT HAVUZ, ölçülü ilk boyama ve
   görsel-hazır geçiş (decodeTiles).

   Önceki sürümlerden korunanlar
   -----------------------------
   v4.3: Favoriler (♡) cihaz/tarayıcı bazlı kalıcı liste (localStorage). Hesap
         senkronu yok — o ayrı bir faz.
   v4.5 / 11. tur (KORUNDU, yeni DOM'a uyarlandı):
     • root'a boş/pasif `touchstart` dinleyicisi — iOS Safari/WebKit'in "ilk dokunuş
       :hover, ikinci dokunuş tıklama" tuhaflığını devre dışı bırakır. Mobilde tek
       dokunuşla açılmanın kök çözümü budur; işlevsel bir davranış EKLEMEZ.
     • attachModalGestures() — modalin herhangi bir yerinden sürükleyince kapanma
       (sadece dokunmatik/kalem; masaüstünde metin seçimini bozmamak için fare hariç)
       ve görsele dokununca ürün sayfasına gitme.
     • balancedSelection() — mozaiği koleksiyonlar arasında round-robin dağıtır,
       tek bir stilin sahneyi doldurmasını engeller.

   V5'ten gelenler
   ---------------
     • presentation: 'gallery' | 'encounter' | 'curation' — tek section, üç sahne.
     • composeRoom() — kürasyon sahnesinde renk/stil yakınlığına göre ve
       yatay/dikey ritim gözeterek eser seçer.
     • Sabitlenen slotlar — kullanıcı duvardaki bir esere kendi seçimini sabitler,
       sessionStorage'da saklanır, otomatik yenileme onu değiştirmez.
     • Sayfalı besleme — katalog artık HTML'e gömülmüyor; Section Rendering API
       üzerinden `cercevo-explore-feed` bölümünden çekiliyor.

   Hibrit havuz
   ------------
   Sorun: V5 saf sayfalı beslemeye geçince her "Yeni karşılaşmalar" tıklaması bir
   ağ isteğine dönüşüyordu; havuz 24 üründe kaldığı için tekrarsızlık defteri de
   anlamını yitiriyordu. İstenen davranış ise "tek load'da yüklenip çalışan" bir alan.
   Çözüm:
     • Liquid tarafında bir tohum (seed) gömülür (~24 eser).
     • `known` Map'i çekilen HER sayfayı biriktirir — havuz sayfa sayfa büyür,
       küçülmez. Tekrarsızlık defteri bu BİRLEŞİK havuz üzerinde çalışır.
     • İlk etkileşimden ÖNCE, requestIdleCallback ile arka planda 2 sayfa daha
       sessizce çekilir. Kullanıcı butona bastığında havuz zaten geniştir.
     • randomize() önce BELLEKTEKİ görülmemiş eserlerden seçer (anında, ağ yok).
   Sonuç: HTML küçük kalır (ürün sayısından bağımsız), etkileşimler anında olur,
   tekrarsızlık tüm katalog boyunca korunur.

   Ölçülü ilk boyama
   -----------------
   Galeri sahnesinde yalnızca ilk iki karo `eager` + `fetchpriority=high` yüklenir;
   kalanlar `lazy`. Yıldız tozu DOM'u (99 düğüm) tüm sunumlarda üretilir — bkz.
   "YILDIZ TOZU RAYI (12 Eylül 2026)" notu, buildStarfield() üzerinde.

   Görsel-hazır geçiş (YENİ, 9 Eylül 2026)
   ---------------------------------------
   Karo değişiminde yeni görsel DOM'a girdiği anda henüz çözülmemiş oluyordu;
   çerçeve bir kare boyunca boş görünüp sonra eser "patlıyordu". Artık her yeni
   karo önce bellekte yüklenip decode ediliyor; eski karo o ana kadar ekranda
   kalıyor. Ağ yavaşsa 700 ms sonra yine de geçiş yapılır — sahne asla donmaz.
   ------------------------------------------------------------------------------ */
(() => {
  "use strict";

  const FEED_PAGE_SIZE = 24;
  const PREFETCH_PAGES = 2;
  const DRAG_THRESHOLD = 20;
  const DECODE_TIMEOUT = 700;

  const randomIndex = (length) => Math.floor(Math.random() * length);

  /* Gerçek Fisher–Yates. Önceki `sort(() => Math.random() - 0.5)` istatistiksel
     olarak bozuktu: bazı sıralamalar diğerlerinden çok daha olasıydı. */
  const uniqueShuffle = (items) => {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = randomIndex(i + 1);
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  const productIdFromGid = (gid = "") => gid.split("/").pop() || "";

  /* Shopify CDN görselleri `?width=N` ile yeniden boyutlanır. Liquid tarafında tek
     bir URL (720 px) gömüyoruz; karolar ekranda ~170 px kapladığı için burada
     istenen genişliğe indiriyoruz. Böylece hem HTML tek URL taşıyor hem de
     tarayıcı gereğinden büyük görsel indirmiyor — mozaik LCP'sinde belirgin fark.
     Shopify CDN dışı bir URL gelirse dokunulmadan geri döner. */
  const sized = (url, width) => {
    if (!url || typeof url !== "string") return url;
    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.searchParams.has("width")) return url;
      parsed.searchParams.set("width", String(width));
      return parsed.href;
    } catch (_) {
      return url;
    }
  };

  const idle = (fn) =>
    typeof window.requestIdleCallback === "function"
      ? window.requestIdleCallback(fn, { timeout: 2500 })
      : window.setTimeout(fn, 900);

  /* Bir karo grubundaki görselleri bellekte yükleyip decode eder. DOM'a hiç
     dokunmaz; çağıran taraf söz (promise) çözüldüğünde geçişi yapar. */
  const decodeTiles = (tiles) => {
    const jobs = tiles
      .map((tile) => tile.querySelector("img"))
      .filter((img) => img && img.src)
      .map(
        (img) =>
          new Promise((resolve) => {
            const pre = new Image();
            pre.decoding = "async";
            const done = () => resolve();
            pre.onload = () => (pre.decode ? pre.decode().then(done, done) : done());
            pre.onerror = done;
            pre.src = img.src;
            if (pre.complete) pre.onload();
          })
      );
    if (!jobs.length) return Promise.resolve();
    return Promise.race([
      Promise.all(jobs),
      new Promise((resolve) => window.setTimeout(resolve, DECODE_TIMEOUT)),
    ]);
  };

  function parseProducts(scope) {
    return Array.from(scope.querySelectorAll("[data-cercevo-product]")).flatMap((node) => {
      try {
        return [JSON.parse(node.textContent)];
      } catch (_) {
        return [];
      }
    }).filter((product) => product && product.id && product.title);
  }

  function buildFavoriteButton(product) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "cercevo-favorite-button";
    button.dataset.cercevoFavorite = "";
    button.innerHTML =
      '<span class="cercevo-favorite-button__icon" aria-hidden="true">♡</span>' +
      '<span class="cercevo-favorite-button__label">Seçkime ekle</span>';
    setFavoriteProduct(button, product);
    return button;
  }

  function setFavoriteProduct(button, product) {
    if (!button || !product) return;
    button.dataset.productId = product.id;
    button.dataset.productGid = product.gid;
    button.dataset.productTitle = product.title;
    button.setAttribute("aria-label", `${product.title} eserini seçkinize ekleyin`);
    button.setAttribute("aria-pressed", "false");
  }

  /* v4.5'ten: havuzu koleksiyona göre gruplayıp gruplar arasında sırayla dolaşır,
     böylece mozaikte tek bir stil sahneyi domine etmez. */
  function balancedSelection(pool, count) {
    const groups = new Map();
    pool.forEach((product) => {
      const key = product.collectionTitle || "Diğer";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(product);
    });
    const shuffledGroups = uniqueShuffle([...groups.values()].map((group) => uniqueShuffle(group)));
    const result = [];
    let exhausted = false;
    while (result.length < count && !exhausted) {
      exhausted = true;
      for (const group of shuffledGroups) {
        if (result.length >= count) break;
        const item = group.shift();
        if (item) {
          result.push(item);
          exhausted = false;
        }
      }
    }
    return result;
  }

  /* V5'ten: kürasyon sahnesi için bir "oda" kurar. Bir tohum eser seçer, renk ve
     stil etiketlerinde onunla örtüşen eserleri puanlar, eşit puanlılar arasında
     yatay/dikey ritmi bozmayanı tercih eder. */
  function composeRoom(pool, count, seedIndex = 0) {
    const unique = [...new Map(
      pool.filter((p) => p.available && p.image).map((p) => [String(p.id), p])
    ).values()];
    if (!unique.length) return [];
    const seed = unique[((seedIndex % unique.length) + unique.length) % unique.length];
    const keys = (product) =>
      (product.tags || [])
        .map((tag) => String(tag).toLocaleLowerCase("tr").trim())
        .filter((tag) => /^(renk|color|palette|stil|style|tema)[:_ -]/.test(tag));
    const seedKeys = new Set(keys(seed));
    const score = (product) =>
      keys(product).reduce(
        (sum, key) => sum + (seedKeys.has(key) ? (/^(renk|color|palette)/.test(key) ? 4 : 2) : 0),
        0
      );
    const ranked = unique.filter((p) => p !== seed).sort((a, b) => score(b) - score(a));
    const selected = [seed];
    while (selected.length < count && ranked.length) {
      const best = score(ranked[0]);
      const opposite = ranked.findIndex(
        (p) =>
          score(p) === best &&
          (Number(p.aspectRatio) > 1) !== (Number(selected[selected.length - 1].aspectRatio) > 1)
      );
      selected.push(ranked.splice(opposite < 0 ? 0 : opposite, 1)[0]);
    }
    return selected;
  }

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = "true";

    const presentation = root.dataset.presentation || "curation";
    const isGallery = presentation === "gallery";
    const isCuration = presentation === "curation";
    const isEncounter = presentation === "encounter";

    /* ---- HİBRİT HAVUZ ---------------------------------------------------- */
    /* `known`: bu filtre için şimdiye kadar görülmüş TÜM eserler (tohum + çekilen
       sayfalar). Küçülmez, sadece büyür. `allProducts` bunun dizi görünümü. */
    let known = new Map();
    let allProducts = [];
    let fetchedPages = new Set();
    let poolTotal = 0;
    let prefetchRunning = false;

    const rebuild = () => {
      allProducts = [...known.values()];
    };

    const mergeProducts = (products) => {
      let added = 0;
      (products || []).forEach((product) => {
        const key = String(product.id);
        if (!known.has(key)) added += 1;
        known.set(key, product);
      });
      rebuild();
      return added;
    };

    const resetPool = (products, total) => {
      known = new Map();
      fetchedPages = new Set();
      mergeProducts(products);
      poolTotal = Math.max(Number(total) || 0, allProducts.length);
    };

    const totalPages = () => Math.max(1, Math.ceil((poolTotal || allProducts.length) / FEED_PAGE_SIZE));
    const unfetchedPages = () => {
      const pages = [];
      for (let page = 1; page <= totalPages(); page += 1) {
        if (!fetchedPages.has(page)) pages.push(page);
      }
      return pages;
    };

    const seed = parseProducts(root);
    resetPool(seed, Number(root.dataset.poolTotal || seed.length));
    /* Tohum, beslemenin 1. sayfasıyla birebir aynı olmayabilir (Liquid ve feed
       farklı sıralama uygulayabilir), o yüzden 1. sayfa "çekilmiş" sayılmaz —
       prefetch onu da alıp havuza katsın. Kayıt `known` Map'inde tekilleşiyor. */

    /* ---- GALERİ KATEGORİ ROTASYONU (16 Eylül 2026, üçüncü güncelleme) ----- */
    /* Sorun: galeri VE Rastlantı Eseri (encounter) sahneleri havuzu
       genişletirken (prefetch, "Yeni karşılaşmalar", reel kaydırma)
       collections.all'ı sayfa sayfa geziyordu — collections.all'ın varsayılan
       sıralaması ALFABETİK olduğu için bu hep aynı "A" harfiyle başlayan ve
       tırnak-işaretli tipografi ürünlerinde takılı kalıyordu (bkz. 1. sayfa
       her zaman aynı ~24 ürün). Çözüm: yalnızca gallery VE encounter
       sunumlarında, section.liquid'in bastığı gerçek stil koleksiyonları
       arasında AĞIRLIKLI bag-shuffle ile dönüşümlü geziliyor; her koleksiyon
       kendi sayfa numaralarını ayrı takip ediyor. Üç katman:
         • poolHandles (data-pool-collections) — kanvas kategorileri, tam ağırlık.
         • reducedHandles (data-reduced-collections) — Cam Tablo/Cam Saat;
           kullanıcı geri bildirimiyle (cam saat çok sık görünüyordu) kanvas
           kategorilerinin data-reduced-weight'i (%) kadar ağırlıkta.
         • rareHandles (data-rare-collections) — Yazı & Tipografi; tamamen
           dışlanmıyor, data-rare-weight ile nadir (varsayılan 1/20) seçiliyor.
       curation bu bloğa hiç girmez — mekân seçiciyle beslenen eski
       collections.all/seed_collection tabanlı akış DEĞİŞMEDEN çalışmaya
       devam ediyor (kullanıcı şikayeti curation'da yoktu). */
    const poolHandles = (root.dataset.poolCollections || "").split(",").filter(Boolean);
    const reducedHandles = (root.dataset.reducedCollections || "").split(",").filter(Boolean);
    const reducedWeightPct = Math.max(1, Math.min(100, Number(root.dataset.reducedWeight || 30)));
    const rareHandles = (root.dataset.rareCollections || "").split(",").filter(Boolean);
    const rareWeight = Math.max(1, Number(root.dataset.rareWeight || 20));
    const categoryState = new Map();
    let categoryQueue = [];

    /* Ağırlıklı torba: tam ağırlıklı kategoriler BASE kez, indirgenmiş
       kategoriler (Cam Tablo/Cam Saat) BASE * reducedWeightPct/100 kez
       torbaya eklenir — sonra Fisher–Yates ile karılır. Böylece bir tam
       tur boyunca indirgenmiş kategoriler, tam ağırlıklılara göre orantılı
       olarak daha az çekilir (hedef: kanvasların ~1/5–2/5'i). */
    const CATEGORY_QUEUE_BASE = 10;
    const buildCategoryQueue = () => {
      const bag = [];
      poolHandles.forEach((handle) => {
        for (let i = 0; i < CATEGORY_QUEUE_BASE; i += 1) bag.push(handle);
      });
      const reducedReps = Math.max(1, Math.round((CATEGORY_QUEUE_BASE * reducedWeightPct) / 100));
      reducedHandles.forEach((handle) => {
        for (let i = 0; i < reducedReps; i += 1) bag.push(handle);
      });
      return uniqueShuffle(bag);
    };

    const getCategoryState = (handle) => {
      if (!categoryState.has(handle)) categoryState.set(handle, { fetchedPages: new Set(), total: null });
      return categoryState.get(handle);
    };

    const categoryURL = (handle, page) => {
      const base = root.dataset.collectionsBase || "/collections/";
      const url = new URL(base + encodeURIComponent(handle), window.location.origin);
      if (page > 1) url.searchParams.set("page", String(page));
      return url.href;
    };

    const nextCategoryPage = (cs) => {
      if (!cs.fetchedPages.has(1)) return 1;
      if (cs.total !== null) {
        const pages = Math.max(1, Math.ceil(cs.total / FEED_PAGE_SIZE));
        const candidates = [];
        for (let p = 1; p <= pages; p += 1) if (!cs.fetchedPages.has(p)) candidates.push(p);
        return candidates.length ? candidates[randomIndex(candidates.length)] : null;
      }
      return cs.fetchedPages.size + 1;
    };

    const nextCategoryHandle = () => {
      if (!poolHandles.length && !reducedHandles.length) return null;
      if (rareHandles.length && randomIndex(rareWeight) === 0) {
        return rareHandles[randomIndex(rareHandles.length)];
      }
      if (!categoryQueue.length) categoryQueue = buildCategoryQueue();
      return categoryQueue.shift();
    };

    /* Bir sonraki (henüz tükenmemiş) kategori + sayfa çiftini bulur. Tüm
       kategoriler o an tükenmişse null döner — nadir bir durum, havuz onlarca
       eser içerdiği için pratikte neredeyse hiç olmaz. Ağırlıklı torbada aynı
       kategori birden fazla kez geçebildiği için deneme sınırı cömert tutulur. */
    const gallerySelectFetch = () => {
      const tried = new Set();
      const uniqueHandleCount = poolHandles.length + reducedHandles.length + rareHandles.length;
      const maxAttempts = uniqueHandleCount * CATEGORY_QUEUE_BASE + rareHandles.length + 5;
      for (let attempts = 0; attempts < maxAttempts && tried.size < uniqueHandleCount; attempts += 1) {
        const handle = nextCategoryHandle();
        if (!handle || tried.has(handle)) continue;
        tried.add(handle);
        const cs = getCategoryState(handle);
        const page = nextCategoryPage(cs);
        if (page !== null) return { handle, page, cs };
      }
      return null;
    };

    const fetchGalleryCategoryPage = async (handle, page, cs, revision) => {
      cs.fetchedPages.add(page);
      try {
        const result = await window.CercevoExperience.feed(categoryURL(handle, page));
        if (revision !== requestRevision) return false;
        mergeProducts(result.products);
        cs.total = Number(result.total) || cs.total || 0;
        poolTotal = Math.max(poolTotal, allProducts.length);
        return true;
      } catch (_) {
        return false;
      }
    };

    let requestRevision = 0;
    let filterURL = root.dataset.feedUrl || "/collections/all";
    let inView = false;
    let roomReady = !isCuration;
    let roomRequested = false;

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");

    /* Sabitlenen duvar slotları — kullanıcının bir konuma kendi seçtiği eser.
       Otomatik yenileme bu slotları asla değiştirmez. */
    const pinned = new Map();
    try {
      const saved = JSON.parse(sessionStorage.getItem("cercevo:wall:" + root.id) || "[]");
      for (const [slot, product] of saved) {
        if (Number.isInteger(slot) && slot >= 0 && slot < 6 && product && product.id) {
          pinned.set(slot, product);
        }
      }
    } catch (_) {
      /* sessionStorage yoksa sabitleme sadece sayfa ömrü boyunca yaşar. */
    }

    const seenStorageKey = `cercevo:discovery:seen:${window.location.hostname}:${root.id}`;
    /* Favoriler site genelinde tek liste olsun diye root.id'ye değil sadece
       hostname'e bağlı. Hesap senkronu YOK — sadece bu cihazda kalıcı. */
    const favoritesStorageKey = `cercevo:favorites:${window.location.hostname}`;

    const ui = {
      root,
      mosaic: root.querySelector("[data-mosaic]"),
      mosaicStatus: root.querySelector("[data-mosaic-status]"),
      filterSummary: root.querySelector("[data-filter-summary]"),
      filtersSecondary: root.querySelector("[data-filters-secondary]"),
      filtersToggle: root.querySelector("[data-filters-toggle]"),
      banner: root.querySelector("[data-banner]"),
      modal: root.querySelector("[data-product-modal]"),
      tileCount: isCuration || window.matchMedia("(max-width: 749px)").matches ? 3 : 5,
      mosaicStagger: Math.max(0, Number(root.dataset.mosaicStagger || 20)),
    };

    const state = {
      activeFilter: "all",
      current: null,
      favorites: new Set(),
      autoplay: root.dataset.autoplay === "true",
      timer: null,
      seenProductIds: new Set(),
      cycleRestarted: false,
    };

    if (!allProducts.length) {
      if (ui.mosaicStatus) ui.mosaicStatus.textContent = "KOLEKSİYON SEÇİLMELİ";
      return;
    }

    const currentPool = () => {
      let pool = allProducts;
      if (state.activeFilter !== "all") {
        pool = allProducts.filter((product) =>
          (product.collectionHandles || [product.collectionHandle]).includes(state.activeFilter)
        );
      }
      const available = pool.filter((product) => product.available);
      return available.length ? available : pool;
    };

    const productFor = (id) => known.get(String(id));

    /* ---- Tekrarsızlık defteri (birleşik havuz üzerinde) ------------------- */
    function loadSeenProducts() {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(seenStorageKey) || "[]");
        if (Array.isArray(saved)) state.seenProductIds = new Set(saved.map(String));
      } catch (_) {
        state.seenProductIds = new Set();
      }
    }

    function persistSeenProducts() {
      try {
        window.sessionStorage.setItem(seenStorageKey, JSON.stringify([...state.seenProductIds]));
      } catch (_) {
        /* sessionStorage yoksa algoritma sayfa ömrü boyunca yine tekrarsız çalışır. */
      }
    }

    function unseenIn(pool) {
      return pool.filter((product) => !state.seenProductIds.has(String(product.id)));
    }

    function chooseUnseenProduct(pool) {
      let candidates = unseenIn(pool);
      state.cycleRestarted = false;
      if (!candidates.length) {
        pool.forEach((product) => state.seenProductIds.delete(String(product.id)));
        persistSeenProducts();
        candidates = [...pool];
        state.cycleRestarted = true;
      }
      if (candidates.length > 1 && state.current) {
        const withoutCurrent = candidates.filter(
          (product) => String(product.id) !== String(state.current.id)
        );
        if (withoutCurrent.length) candidates = withoutCurrent;
      }
      const selected = candidates[randomIndex(candidates.length)] || pool[0];
      if (selected) {
        state.seenProductIds.add(String(selected.id));
        persistSeenProducts();
      }
      return selected;
    }

    function activeFilterLabel() {
      const escape = window.CSS && CSS.escape ? CSS.escape(state.activeFilter) : state.activeFilter;
      const button = root.querySelector(`[data-filter="${escape}"]`);
      return (button && button.textContent.trim()) || "Tümü";
    }

    function updateSessionSummary() {
      if (!ui.filterSummary) return;
      const pool = currentPool();
      const label = activeFilterLabel();
      const remaining = unseenIn(pool).length;
      const known_total = poolTotal || pool.length;
      if (state.cycleRestarted) {
        ui.filterSummary.textContent = `${label} seçkisinde tüm eserler gösterildi; yeni tur başladı.`;
      } else {
        ui.filterSummary.textContent = `${known_total} eser içinden seçiliyor — ${label} seçkisinde bu oturumda henüz gösterilmemiş ${remaining} eser hazır.`;
      }
    }

    /* ---- Favoriler (Seçkim) ---------------------------------------------- */
    function syncFavoriteButtons() {
      root.querySelectorAll("[data-cercevo-favorite]").forEach((button) => {
        const id = String(button.dataset.productId || productIdFromGid(button.dataset.productGid));
        const isFavorited = state.favorites.has(id);
        button.classList.toggle("is-favorited", isFavorited);
        button.setAttribute("aria-pressed", String(isFavorited));
        const label = button.querySelector(".cercevo-favorite-button__label");
        if (label) label.textContent = isFavorited ? "Seçkimden çıkar" : "Seçkime ekle";
        const icon = button.querySelector(".cercevo-favorite-button__icon");
        if (icon) icon.textContent = isFavorited ? "♥" : "♡";
        const title = button.dataset.productTitle || "Bu eser";
        button.setAttribute(
          "aria-label",
          `${title} eserini seçkinizden ${isFavorited ? "çıkarın" : "ekleyin"}`
        );
      });
    }

    function loadFavoritesLocal() {
      try {
        const saved = JSON.parse(window.localStorage.getItem(favoritesStorageKey) || "[]");
        if (Array.isArray(saved)) state.favorites = new Set(saved.map(String));
      } catch (_) {
        state.favorites = new Set();
      }
    }

    function persistFavoritesLocal() {
      try {
        window.localStorage.setItem(favoritesStorageKey, JSON.stringify([...state.favorites]));
      } catch (_) {
        /* Gizli sekmede favoriler sadece sayfa ömrü boyunca kalır. */
      }
    }

    function toggleFavorite(button) {
      const product = productFor(button.dataset.productId);
      if (!product) return;
      const id = String(product.id);
      if (state.favorites.has(id)) state.favorites.delete(id);
      else state.favorites.add(id);
      persistFavoritesLocal();
      syncFavoriteButtons();
      document.dispatchEvent(new CustomEvent("cercevo:favorites-change"));
    }

    /* ---- Rastlantı Eseri banner'ı ---------------------------------------- */
    function setBanner(product) {
      if (!product || !ui.banner) return;
      state.current = product;
      const image = ui.banner.querySelector("[data-banner-image]");
      const fallback = ui.banner.querySelector("[data-banner-fallback]");
      const media = ui.banner.querySelector("[data-modal-open]");
      const updateText = (selector, value) => {
        const element = ui.banner.querySelector(selector);
        if (element) element.textContent = value || "—";
      };
      updateText("[data-banner-title]", product.title);
      updateText("[data-banner-vendor]", product.vendor);
      updateText("[data-banner-collection]", product.collectionTitle);
      updateText("[data-banner-price]", product.price);
      const bannerImage = product.cardImage || product.image;
      if (image && bannerImage) {
        image.src = sized(bannerImage, 1000);
        image.alt = product.cardImageAlt || product.imageAlt || product.title;
        image.hidden = false;
        if (fallback) fallback.hidden = true;
      } else if (fallback) {
        fallback.hidden = false;
        if (image) image.hidden = true;
      }
      if (media) media.setAttribute("aria-label", `${product.title} eserinin detaylarını aç`);
      const holder = ui.banner.querySelector("[data-cercevo-favorite-holder]");
      if (holder) {
        holder.replaceChildren(buildFavoriteButton(product));
      } else {
        setFavoriteProduct(ui.banner.querySelector("[data-cercevo-favorite]"), product);
      }
      syncFavoriteButtons();
    }

    /* Menüdeki Rastgele Eser kısayolu reel kapanınca bu kartı son görülen
       eserle eşitler. Böylece menüden başlayan keşif de sayfadaki karşılaşma
       hikâyesinin doğal devamı olur. */
    if (isEncounter) root.cercevoSetEncounter = setBanner;

    /* ---- Mozaik ----------------------------------------------------------- */
    let tileIndex = 0;

    const makeTile = (product, slot = 0) => {
      const tile = document.createElement("div");
      tile.className = "cercevo-discovery__tile is-changing";
      tile.dataset.productId = product.id;
      tile.dataset.slot = String(slot);
      const productKind = `${product.type || ""} ${product.title || ""}`.toLocaleLowerCase("tr");
      if (productKind.includes("saat")) tile.classList.add("is-clock");
      tile.style.animationDelay = `${Math.min(slot * ui.mosaicStagger, 400)}ms`;

      if (product.image) {
        const frame = document.createElement("button");
        frame.type = "button";
        frame.className = "cx-art-frame";
        frame.setAttribute("aria-label", `${product.title} — Tam ekran keşfet`);
        const ratio = Number(product.aspectRatio) || 0.667;
        frame.style.setProperty("--art-ratio", String(ratio));

        const image = document.createElement("img");
        image.addEventListener("load", () => {
          if (image.naturalWidth && image.naturalHeight) {
            frame.style.setProperty("--art-ratio", String(image.naturalWidth / image.naturalHeight));
          }
        });
        /* Galeri mozaik karoları küçük kalır; kürasyon sahnesindeki tablolar
           masaüstünde geniş yer kapladığı için Shopify CDN'den daha yüksek
           çözünürlük ister. Yalnızca kürasyonun üç görseli yükseltilir. */
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const roomImageWidth = Math.min(
          1600,
          Math.max(600, Math.ceil((document.documentElement.clientWidth || window.innerWidth) * 0.42 * dpr))
        );
        image.src = sized(product.image, isCuration ? roomImageWidth : 400);
        image.alt = "";
        image.decoding = "async";
        /* Ölçülü ilk boyama: galeri sahnesinde yalnızca ilk iki karo öncelikli. */
        if (isGallery && tileIndex < 2) {
          image.loading = "eager";
          image.setAttribute("fetchpriority", "high");
        } else {
          image.loading = "lazy";
          image.setAttribute("fetchpriority", "low");
        }
        image.width = 360;
        image.height = Math.round(360 / ratio);
        tileIndex += 1;
        frame.append(image);
        tile.append(frame);
      }

      const label = document.createElement("span");
      label.className = "cercevo-discovery__tile-tag";
      label.textContent = product.title;
      tile.append(label);

      if (isGallery) {
        const search = document.createElement("button");
        search.type = "button";
        search.className = "cx-wall-search";
        search.textContent = "⌕";
        search.setAttribute("aria-label", `${product.title} yerine eser ara`);
        search.addEventListener("click", (event) => {
          event.stopPropagation();
          root.dispatchEvent(
            new CustomEvent("cercevo:wall-search", { bubbles: true, detail: { slot, product } })
          );
        });
        tile.append(search);
      }

      tile.addEventListener("click", () => openModal(product));
      return tile;
    };

    function mosaicUniverse() {
      return currentPool();
    }

    function renderMosaic(single = false) {
      if (!ui.mosaic) return;
      if (!roomReady) {
        ui.mosaic.replaceChildren();
        return;
      }
      if (isEncounter) return;

      const pool = mosaicUniverse().filter((p) => p.image);
      if (!pool.length) {
        ui.mosaic.replaceChildren();
        return;
      }

      const visible = new Set([...ui.mosaic.children].map((node) => node.dataset.productId));
      const candidates = uniqueShuffle(pool.filter((p) => !visible.has(String(p.id))));

      /* Tek karo yenileme (otomatik akış): sabitlenmiş slotlara dokunulmaz.
         Yeni görsel hazır olana kadar eski karo ekranda kalır. */
      if (single && ui.mosaic.children.length) {
        if (!candidates.length) return;
        const slots = [...ui.mosaic.children].map((_, i) => i).filter((i) => !pinned.has(i));
        if (!slots.length) return;
        const index = slots[randomIndex(slots.length)];
        const chosen = candidates[0];
        state.seenProductIds.add(String(chosen.id));
        persistSeenProducts();
        const nextTile = makeTile(chosen, index);
        const oldTile = ui.mosaic.children[index];
        decodeTiles([nextTile]).then(() => {
          if (oldTile && oldTile.isConnected) oldTile.replaceWith(nextTile);
        });
        return;
      }

      /* Tam sahne: kürasyonda oda kompozisyonu, diğerlerinde dengeli dağılım. */
      const unseen = unseenIn(pool);
      const source = unseen.length >= ui.tileCount ? unseen : pool;
      const selection = isCuration
        ? composeRoom(source, ui.tileCount, randomIndex(source.length))
        : balancedSelection(source, ui.tileCount);

      const free = selection.filter(
        (p) => ![...pinned.values()].some((saved) => String(saved.id) === String(p.id))
      );
      let cursor = 0;
      tileIndex = 0;
      const tiles = selection.map((product, index) =>
        makeTile(pinned.get(index) || free[cursor++] || product, index)
      );
      /* İlk boyamada beklenmez — sahne hemen kurulur. Sonraki her tam yenilemede
         görseller çözülene kadar eski sahne ekranda kalır; böylece "boş çerçeve"
         karesi hiç oluşmaz. */
      if (ui.mosaic.children.length) {
        decodeTiles(tiles).then(() => ui.mosaic.replaceChildren(...tiles));
      } else {
        ui.mosaic.replaceChildren(...tiles);
      }
      selection.forEach((product) => state.seenProductIds.add(String(product.id)));
      persistSeenProducts();
      if (ui.mosaicStatus) ui.mosaicStatus.textContent = "";
    }

    /* ---- Modal ------------------------------------------------------------ */
    function openModal(product = state.current) {
      if (!product) return;
      /* Site geneli keşif motoru varsa tam ekran reel'i o açar. */
      if (window.CercevoExperience) {
        window.CercevoExperience.remember(allProducts);
        /* Galeri VEYA Rastlantı Eseri sahnesinde reel'i bu sahnenin kendi
           çeşitlendirilmiş havuzuyla tohumla — aksi halde reel kendi başına
           filterURL'den (genelde collections/all, ALFABETİK sıralı) sırayla
           sayfalar ve kategori rotasyonundan habersiz kalır (Rastlantı
           Eseri'nde bu, hep aynı "A" harfiyle başlayan/tipografi ürünlerinde
           takılı kalma şikayetine yol açıyordu). Curation bu koşula girmez. */
        const seedProducts =
          (isGallery || isEncounter) && allProducts.length
            ? uniqueShuffle(allProducts.filter((p) => String(p.id) !== String(product.id)))
            : null;
        window.CercevoExperience.open(product, {
          url: filterURL,
          source: setBanner,
          products: seedProducts,
        });
        return;
      }
      if (!ui.modal) return;
      const set = (selector, value) => {
        const element = ui.modal.querySelector(selector);
        if (element) element.textContent = value || "—";
      };
      const image = ui.modal.querySelector("[data-modal-image]");
      const fallback = ui.modal.querySelector("[data-modal-fallback]");
      set("[data-modal-title]", product.title);
      set("[data-modal-vendor]", product.vendor);
      set("[data-modal-price]", product.price);
      set("[data-modal-collection]", product.collectionTitle);
      const productLink = ui.modal.querySelector("[data-modal-product-link]");
      if (productLink) productLink.href = product.url;
      if (image && product.image) {
        image.src = product.image;
        image.alt = product.imageAlt || product.title;
        image.hidden = false;
        if (fallback) fallback.hidden = true;
      } else if (fallback) {
        fallback.hidden = false;
        if (image) image.hidden = true;
      }
      const favoriteHolder = ui.modal.querySelector("[data-modal-favorite]");
      if (favoriteHolder) {
        favoriteHolder.innerHTML = "";
        favoriteHolder.append(buildFavoriteButton(product));
      }
      syncFavoriteButtons();
      if (typeof ui.modal.showModal === "function" && !ui.modal.open) ui.modal.showModal();
      else ui.modal.setAttribute("open", "open");
    }

    /* 11. tur (KORUNDU): modal jestleri. Sürükleme ile kapatma yalnızca
       dokunmatik/kalem girişte — masaüstünde fare ile metin seçerken kartın
       yanlışlıkla kapanmaması için. Sürükleme yoksa ve dokunulan yer görselse
       ürün sayfasına gidilir. */
    function attachModalGestures() {
      if (!ui.modal) return;
      let start = null;
      ui.modal.addEventListener("pointerdown", (event) => {
        start = {
          x: event.clientX,
          y: event.clientY,
          pointerType: event.pointerType,
          target: event.target,
        };
      });
      ui.modal.addEventListener("pointerup", (event) => {
        if (!start) return;
        const dragged = Math.hypot(event.clientX - start.x, event.clientY - start.y) > DRAG_THRESHOLD;
        const onMedia =
          start.target && start.target.closest && start.target.closest(".cercevo-discovery__modal-media");
        if (dragged) {
          if (start.pointerType !== "mouse" && typeof ui.modal.close === "function") ui.modal.close();
        } else if (onMedia) {
          const link = ui.modal.querySelector("[data-modal-product-link]");
          const href = link && link.getAttribute("href");
          if (href && href !== "#") window.location.href = link.href;
        }
        start = null;
      });
      ui.modal.addEventListener("pointercancel", () => {
        start = null;
      });
    }

    /* ---- Besleme (Section Rendering API üzerinden) ------------------------ */
    async function fetchPage(url, revision, { replace = false } = {}) {
      if (!window.CercevoExperience) return false;
      root.setAttribute("aria-busy", "true");
      try {
        const result = await window.CercevoExperience.feed(url);
        if (revision !== requestRevision) return false;
        if (replace) resetPool(result.products, result.total);
        else {
          mergeProducts(result.products);
          poolTotal = Math.max(poolTotal, Number(result.total) || 0);
        }
        if (!currentPool().length) {
          if (ui.filterSummary) ui.filterSummary.textContent = "Bu seçimle eşleşen eser bulunamadı.";
          return false;
        }
        return true;
      } catch (_) {
        if (ui.filterSummary) {
          ui.filterSummary.textContent =
            "Eserler yüklenemedi. Önceki seçki korunuyor — Yeni karşılaşmalar ile tekrar deneyin.";
        }
        return false;
      } finally {
        if (revision === requestRevision) root.removeAttribute("aria-busy");
      }
    }

    function pageURL(page) {
      const url = new URL(filterURL, window.location.origin);
      if (page > 1) url.searchParams.set("page", String(page));
      else url.searchParams.delete("page");
      return url.href;
    }

    /* Arka plan prefetch: kullanıcı henüz hiçbir şeye basmadan havuzu genişletir.
       Sessizdir — hata olursa kullanıcıya hiçbir şey gösterilmez, havuz olduğu
       gibi kalır. */
    async function prefetchPool(count = PREFETCH_PAGES) {
      if (prefetchRunning || !window.CercevoExperience) return;
      prefetchRunning = true;
      try {
        if ((isGallery || isEncounter) && (poolHandles.length || reducedHandles.length)) {
          for (let i = 0; i < count; i += 1) {
            const next = gallerySelectFetch();
            if (!next) break;
            const revision = requestRevision;
            const ok = await fetchGalleryCategoryPage(next.handle, next.page, next.cs, revision);
            if (!ok) break;
          }
        } else {
          for (let i = 0; i < count; i += 1) {
            const pending = unfetchedPages();
            if (!pending.length) break;
            const page = pending[randomIndex(pending.length)];
            fetchedPages.add(page);
            const revision = requestRevision;
            const ok = await window.CercevoExperience.feed(pageURL(page))
              .then((result) => {
                if (revision !== requestRevision) return false;
                mergeProducts(result.products);
                poolTotal = Math.max(poolTotal, Number(result.total) || 0);
                return true;
              })
              .catch(() => false);
            if (!ok) break;
          }
        }
        updateSessionSummary();
      } finally {
        prefetchRunning = false;
      }
    }

    /* ---- Yeni karşılaşmalar ---------------------------------------------- */
    async function randomize() {
      const pool = currentPool();
      const unseen = unseenIn(pool);
      const hasMorePages =
        (isGallery || isEncounter) && (poolHandles.length || reducedHandles.length)
          ? true
          : unfetchedPages().length > 0;

      /* Bellekteki görülmemişler yeterliyse: ağa hiç gitme, anında yenile. */
      if (unseen.length >= ui.tileCount || !hasMorePages) {
        setBanner(chooseUnseenProduct(pool));
        renderMosaic();
        updateSessionSummary();
        /* Havuz inceliyorsa arkadan sessizce bir sayfa daha çek. */
        if (unseen.length < ui.tileCount * 3) idle(() => prefetchPool(1));
        return;
      }

      /* Galeri: bir sonraki dönüşümlü kategori sayfasını al (bkz. GALERİ
         KATEGORİ ROTASYONU). Curation/encounter: eski collections.all tabanlı
         akış DEĞİŞMEDEN devam eder. */
      if ((isGallery || isEncounter) && (poolHandles.length || reducedHandles.length)) {
        const next = gallerySelectFetch();
        const revision = ++requestRevision;
        const ok = next ? await fetchGalleryCategoryPage(next.handle, next.page, next.cs, revision) : false;
        if (revision !== requestRevision) return;
        setBanner(chooseUnseenProduct(currentPool()));
        renderMosaic();
        updateSessionSummary();
        return;
      }

      /* Görülmemiş kalmadı ve çekilmemiş sayfa var: bir sonraki sayfayı al. */
      const pending = unfetchedPages();
      const page = pending[randomIndex(pending.length)];
      fetchedPages.add(page);
      const revision = ++requestRevision;
      const ok = await fetchPage(pageURL(page), revision);
      if (revision !== requestRevision) return;
      setBanner(chooseUnseenProduct(currentPool()));
      renderMosaic();
      updateSessionSummary();
      if (!ok) fetchedPages.delete(page);
    }

    /* ---- Filtre / mekân seçimi ------------------------------------------- */
    async function setActiveFilter(filterButton) {
      if (isCuration) roomRequested = true;
      const previous = {
        filter: state.activeFilter,
        url: filterURL,
        known,
        pages: fetchedPages,
        total: poolTotal,
      };
      const next = filterButton.dataset.filter || "all";
      /* Filtre URL'i `data-feed-url`den TÜRETİLMEZ — tohum koleksiyonu artık
         /collections/all olmak zorunda değil (kürasyon sahnesi ilk mekânla
         tohumlanıyor). Bunun yerine mağazanın koleksiyon kök yolundan kurulur. */
      const base = root.dataset.collectionsBase || "/collections/";
      filterURL = base + encodeURIComponent(next);
      state.activeFilter = next;

      const roomNote = root.querySelector("[data-room-note-label]");
      if (roomNote) roomNote.textContent = "Seçki yükleniyor…";

      const revision = ++requestRevision;
      const ok = await fetchPage(pageURL(1), revision, { replace: true });
      if (revision !== requestRevision) return;

      if (!ok) {
        state.activeFilter = previous.filter;
        filterURL = previous.url;
        known = previous.known;
        fetchedPages = previous.pages;
        poolTotal = previous.total;
        rebuild();
        if (roomNote) roomNote.textContent = "Bu mekân seçkisi yüklenemedi. Önceki seçim korunuyor.";
        return;
      }

      fetchedPages.add(1);
      roomReady = true;
      root.querySelectorAll("[data-filter]").forEach((button) => {
        const active = button === filterButton;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      if (filterButton.dataset.roomScene) {
        const stage = root.querySelector(".cercevo-discovery__mosaic-wrap");
        if (stage) {
          stage.style.setProperty(
            "--cx-room-scene",
            `url("${filterButton.dataset.roomScene.replaceAll('"', "%22")}")`
          );
        }
      }
      if (filterButton.dataset.roomNote !== undefined) {
        if (roomNote) roomNote.textContent = filterButton.dataset.roomNote;
        const caption = root.querySelector(".cx-stage-caption");
        if (caption) {
          caption.textContent = filterButton.getAttribute("aria-label") || filterButton.textContent.trim();
        }
      }

      setBanner(chooseUnseenProduct(currentPool()));
      renderMosaic();
      updateSessionSummary();
      idle(() => prefetchPool(1));
    }

    /* ---- Dış API (galeri slaytları ve duvar araması için) ----------------- */
    root.cercevoSetCollection = async (url) => {
      const previous = { known, pages: fetchedPages, total: poolTotal, url: filterURL };
      const revision = ++requestRevision;
      state.activeFilter = "all";
      const ok = await fetchPage(url, revision, { replace: true });
      if (revision !== requestRevision) return false;
      if (ok) {
        filterURL = url;
        fetchedPages.add(1);
        renderMosaic();
        idle(() => prefetchPool(1));
        return true;
      }
      known = previous.known;
      fetchedPages = previous.pages;
      poolTotal = previous.total;
      filterURL = previous.url;
      rebuild();
      return false;
    };

    root.cercevoSetSlot = (slot, product) => {
      pinned.set(slot, product);
      mergeProducts([product]);
      try {
        sessionStorage.setItem("cercevo:wall:" + root.id, JSON.stringify([...pinned]));
      } catch (_) {
        /* Sabitleme sadece sayfa ömrü boyunca yaşar. */
      }
      if (window.CercevoExperience) window.CercevoExperience.remember([product]);
      const node = ui.mosaic && ui.mosaic.children[slot];
      if (node) {
        const nextTile = makeTile(product, slot);
        decodeTiles([nextTile]).then(() => {
          if (node.isConnected) node.replaceWith(nextTile);
        });
      }
    };

    /* ---- Etkileşimler ----------------------------------------------------- */
    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-randomize]")) {
        randomize();
        return;
      }
      const filter = event.target.closest("[data-filter]");
      if (filter) {
        setActiveFilter(filter);
        return;
      }
      const toggle = event.target.closest("[data-filters-toggle]");
      if (toggle && ui.filtersSecondary) {
        const expanded = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", String(!expanded));
        ui.filtersSecondary.hidden = expanded;
        toggle.textContent = expanded ? "+ Stiller ve Temalar" : "− Daha az göster";
        return;
      }
      if (event.target.closest("[data-modal-open]")) {
        openModal();
        return;
      }
      const favorite = event.target.closest("[data-cercevo-favorite]");
      if (favorite) {
        event.preventDefault();
        event.stopPropagation();
        toggleFavorite(favorite);
        return;
      }
      if (event.target.closest("[data-modal-close]") && ui.modal && ui.modal.close) ui.modal.close();
    });

    /* 11. tur (KORUNDU): iOS Safari/WebKit'in "ilk dokunuş :hover, ikinci dokunuş
       tıklama" tuhaflığını devre dışı bırakan boş/pasif touchstart dinleyicisi.
       Mobilde tek dokunuşla açılmanın kök çözümü budur; işlevsel bir davranış
       eklemez, hiçbir olayı tüketmez. */
    root.addEventListener("touchstart", () => {}, { passive: true });

    root.querySelectorAll("[data-toggle-autoplay]").forEach((button) => {
      const pauseIcon = button.querySelector("[data-icon-pause]");
      const playIcon = button.querySelector("[data-icon-play]");
      const syncIcon = () => {
        if (pauseIcon) pauseIcon.hidden = !state.autoplay;
        if (playIcon) playIcon.hidden = state.autoplay;
        button.setAttribute("aria-label", state.autoplay ? "Akışı duraklat" : "Akışı devam ettir");
        button.setAttribute("aria-pressed", String(!state.autoplay));
      };
      syncIcon();
      button.addEventListener("click", () => {
        state.autoplay = !state.autoplay;
        syncIcon();
        schedule();
      });
    });

    /* ---- Otomatik akış ---------------------------------------------------- */
    /* Sadece görünürken, sekme öndeyken, üzerinde gezinilmiyorken ve açık bir
       diyalog yokken çalışır. Encounter sahnesinde otomatik akış yoktur. */
    function schedule() {
      window.clearInterval(state.timer);
      if (isEncounter || !state.autoplay || !inView || document.hidden || motion.matches) return;
      const seconds = Math.max(8, Number(root.dataset.rotationSeconds || 12));
      state.timer = window.setInterval(() => {
        try {
          if (
            !inView ||
            document.hidden ||
            root.matches(":hover") ||
            root.contains(document.activeElement) ||
            document.querySelector("dialog[open]")
          ) {
            return;
          }
          renderMosaic(true);
        } catch (_) {
          /* Bir sonraki tikte yeniden dener; interval canlı kalır. */
        }
      }, seconds * 1000);
    }

    /* ---- Yıldız tozu ------------------------------------------------------ */
    /* YILDIZ TOZU RAYI (12 Eylül 2026): önceden bu fonksiyon `cx-v4` sınıflı
       köklerde (gallery, curation) erken çıkış yapıyordu — o dönem yıldız
       tozu yalnızca encounter sahnesinde gösteriliyordu, rail (üst şerit) ise
       gallery/curation'da onun yerini alıyordu. Artık starfield HER sunumda
       görünür olması istendiği için (bkz. sections/cercevo-collection-discovery.liquid
       üstündeki aynı tarihli not) bu erken çıkış kaldırıldı — cx-v4 kontrolü
       artık gereksiz, HTML tarafında zaten starfield koşulsuz basılıyor. */
    function buildStarfield() {
      const field = root.querySelector("[data-discovery-starfield]");
      if (!field) return;
      const STAR_PATH = "M12 0c0 6.6 5.4 12 12 12-6.6 0-12 5.4-12 12 0-6.6-5.4-12-12-12C6.6 12 12 6.6 12 0z";
      const fragment = document.createDocumentFragment();
      /* Tek kartlık encounter sahnesinde 42 yıldız aynı görsel yoğunluğu verir;
         eski 99 düğümlük alanı burada taşımak gereksiz boyama maliyetiydi. */
      const starCount = isEncounter ? 42 : 99;
      for (let i = 0; i < starCount; i += 1) {
        const star = document.createElement("div");
        star.className = "cercevo-discovery__star";
        if (i % 5 === 0) star.classList.add("cercevo-discovery__star--big");
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 100}%`;
        star.style.animationDelay = `${Math.random() * 3.4}s`;
        star.style.animationDuration = `${2.4 + Math.random() * 1.6}s`;
        star.innerHTML = `<svg viewBox="0 0 24 24"><path fill="currentColor" d="${STAR_PATH}"/></svg>`;
        fragment.append(star);
      }
      field.append(fragment);
    }

    /* ---- Başlangıç -------------------------------------------------------- */
    buildStarfield();
    loadSeenProducts();
    loadFavoritesLocal();
    setBanner(chooseUnseenProduct(currentPool()) || allProducts[0]);
    renderMosaic();
    syncFavoriteButtons();
    updateSessionSummary();
    attachModalGestures();

    const observer = new IntersectionObserver(
      (entries) => {
        inView = entries[0].isIntersecting;
        root.classList.toggle("is-in-view", inView);
        schedule();
      },
      { threshold: 0.15 }
    );
    observer.observe(root);

    const onVisibility = () => schedule();
    document.addEventListener("visibilitychange", onVisibility);

    const onResize = () => {
      const count = isCuration || window.matchMedia("(max-width: 749px)").matches ? 3 : 5;
      if (count !== ui.tileCount) {
        ui.tileCount = count;
        renderMosaic();
      }
    };
    window.addEventListener("resize", onResize);

    const onMotion = () => {
      const field = root.querySelector("[data-discovery-starfield]");
      if (field) field.replaceChildren();
      buildStarfield();
      schedule();
    };
    motion.addEventListener("change", onMotion);

    const onFavoritesChange = () => {
      loadFavoritesLocal();
      syncFavoriteButtons();
    };
    document.addEventListener("cercevo:favorites-change", onFavoritesChange);

    root.cercevoCleanup = () => {
      window.clearInterval(state.timer);
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      motion.removeEventListener("change", onMotion);
      document.removeEventListener("cercevo:favorites-change", onFavoritesChange);
      delete root.cercevoSetEncounter;
    };

    /* Rastlantı Eseri kartına tam ekran / 3D kısayolları. */
    if (ui.banner && !isEncounter) {
      const actions = document.createElement("div");
      actions.className = "cx-card-actions";
      const full = document.createElement("button");
      full.type = "button";
      full.textContent = "⛶ Tam ekran";
      full.addEventListener("click", () => openModal());
      const ar = document.createElement("button");
      ar.type = "button";
      ar.textContent = "3D · Alanınızda görün";
      ar.addEventListener("click", () => openModal());
      actions.append(full, ar);
      ui.banner.append(actions);
    }

    /* Kürasyon sahnesi görünür alana yaklaşınca ilk mekânı yükler; diğer sahneler
       tohumla zaten doludur, sadece arka planda havuzu genişletirler. */
    const warmUp = () => {
      if (roomRequested) return;
      if (isCuration) {
        const first = root.querySelector(".cx-room-mini [data-filter]");
        if (first) {
          setActiveFilter(first);
          return;
        }
        roomReady = true;
        renderMosaic();
      }
      idle(() => prefetchPool());
    };

    if (isCuration) {
      const loader = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loader.disconnect();
            warmUp();
          }
        },
        { rootMargin: "300px" }
      );
      loader.observe(root);
      const cleanup = root.cercevoCleanup;
      root.cercevoCleanup = () => {
        loader.disconnect();
        cleanup();
      };
    } else {
      idle(warmUp);
    }
  }

  const start = () => document.querySelectorAll("[data-cercevo-discovery]").forEach(init);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
  document.addEventListener("shopify:section:load", start);
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-cercevo-discovery]").forEach((root) => {
      if (root.cercevoCleanup) root.cercevoCleanup();
    });
  });
})();
